import { api, idempotencyKey, unwrap } from '../api/client';
import type { components } from '../api/schema.gen';
import {
  employeeFrom,
  eventFrom,
  itemFrom,
  moduleFrom,
  profileToDto,
  requestFrom,
  sessionFrom,
  workspaceFrom,
} from '../domain/mappers';
import type {
  AutonomyKind,
  DecisionKind,
  Employee,
  EmployeeModule,
  EmployeeProfile,
  EmployeeRequest,
  EmployeeRequestInput,
  Item,
  ItemEvent,
  ItemStatus,
  ItemType,
  Page,
  Session,
  Workspace,
} from '../domain/models';
import { echoesApproval } from '../domain/models';

// Every call the app makes, returning domain models. Failures are thrown as AppErrors.

type ItemPage = components['schemas']['ItemPage'];

const page = (p: ItemPage): Page<Item> => ({ items: p.data.map((i) => itemFrom(i)), nextCursor: p.next_cursor ?? undefined });
const blank = (s?: string) => (s?.trim() ? s.trim() : undefined);

export async function me(): Promise<Session> {
  return sessionFrom(unwrap(await api.GET('/v1/me')));
}

export async function workspaces(): Promise<Workspace[]> {
  return unwrap(await api.GET('/v1/workspaces')).data.map(workspaceFrom);
}

/** All employees of the organization. */
export async function employees(): Promise<Employee[]> {
  return unwrap(await api.GET('/v1/employees')).data.map(employeeFrom);
}

export async function employee(id: string): Promise<Employee> {
  return employeeFrom(unwrap(await api.GET('/v1/employees/{employeeId}', { params: { path: { employeeId: id } } })));
}

/** Lets one kind of an employee's work be approved without a person, or brings it back under review.
 * Only offered when `employee.canSetAutonomy` and the kind is `allowed`. Returns the employee as it is now. */
export async function setAutonomy(employeeId: string, kind: AutonomyKind, automatic: boolean): Promise<Employee> {
  const res = await api.PUT('/v1/employees/{employeeId}/autonomy/{kind}', {
    params: { path: { employeeId, kind } },
    body: { automatic },
  });
  return employeeFrom(unwrap(res));
}

/** Replaces what the employee knows about the business. Only offered when `employee.canEditProfile`. */
export async function saveProfile(employeeId: string, profile: EmployeeProfile): Promise<Employee> {
  const res = await api.PUT('/v1/employees/{employeeId}/profile', {
    params: { path: { employeeId } },
    body: profileToDto(profile),
  });
  return employeeFrom(unwrap(res));
}

// ---- Hiring (ADR 0010) ----

/** The employees Talkos offers, in the order the shelf shows them. */
export async function catalog(): Promise<EmployeeModule[]> {
  return unwrap(await api.GET('/v1/catalog')).data.map(moduleFrom);
}

/** Requests for new employees, newest first. */
export async function employeeRequests(): Promise<EmployeeRequest[]> {
  return unwrap(await api.GET('/v1/employee-requests')).data.map(requestFrom);
}

/** Asks the Talkos team for a new employee. Field errors come back on the AppError (`profile.answers.<key>`,
 * `profile.note`, `workspace_name`, `workspace_description`, `title`). */
export async function requestEmployee(r: EmployeeRequestInput): Promise<EmployeeRequest> {
  const res = await api.POST('/v1/employee-requests', {
    body: {
      role: r.role ?? null,
      title: blank(r.title) ?? null,
      workspace_id: r.workspaceId ?? null,
      workspace_name: r.workspaceId ? null : (blank(r.workspaceName) ?? null),
      workspace_description: r.workspaceId ? null : (blank(r.workspaceDescription) ?? null),
      profile: profileToDto(r.profile),
      automatic_kinds: r.automaticKinds,
    },
  });
  return requestFrom(unwrap(res));
}

export interface ItemFilter {
  workspaceId?: string;
  employeeId?: string;
  type?: Exclude<ItemType, 'unknown'>;
  parentId?: string;
  statuses?: Exclude<ItemStatus, 'unknown'>[];
  cursor?: string;
}

/** Work that needs a person: highest priority first, then oldest. */
export async function inbox(f: ItemFilter = {}): Promise<Page<Item>> {
  return list('inbox', f);
}

/** Every item, newest first. */
export async function allItems(f: ItemFilter = {}): Promise<Page<Item>> {
  return list('all', f);
}

/** Items that came from this one, newest first: its tasks, or a task's results. */
export async function related(id: string): Promise<Item[]> {
  return (await list('all', { parentId: id })).items;
}

async function list(view: 'inbox' | 'all', f: ItemFilter): Promise<Page<Item>> {
  const res = await api.GET('/v1/items', {
    params: {
      query: {
        view,
        workspace_id: f.workspaceId,
        employee_id: f.employeeId,
        type: f.type,
        parent_id: f.parentId,
        status: f.statuses,
        cursor: f.cursor,
      },
    },
  });
  return page(unwrap(res));
}

export async function item(id: string): Promise<Item> {
  const res = await api.GET('/v1/items/{itemId}', { params: { path: { itemId: id } } });
  return itemFrom(unwrap(res), res.response.headers.get('ETag') ?? undefined);
}

export async function itemEvents(id: string): Promise<ItemEvent[]> {
  return unwrap(await api.GET('/v1/items/{itemId}/events', { params: { path: { itemId: id } } }))
    .data.map(eventFrom)
    .filter((e) => !echoesApproval(e));
}

/** Decides the exact version the person saw (`item.etag`). With `payload`, the edited content is
 * stored as a new revision and approved. */
export async function decide(
  it: Item,
  kind: DecisionKind,
  opts: { comment?: string; payload?: Record<string, unknown> } = {},
): Promise<Item> {
  const res = await api.POST('/v1/items/{itemId}/decision', {
    params: {
      path: { itemId: it.id },
      header: { 'If-Match': it.etag ?? '', 'Idempotency-Key': idempotencyKey() },
    },
    body: { decision: kind, comment: blank(opts.comment), payload: opts.payload },
  });
  return itemFrom(unwrap(res), res.response.headers.get('ETag') ?? undefined);
}

/** Gives the work in an item to an employee who takes tasks. Returns the new task. */
export async function handOff(itemId: string, employeeId: string, note?: string): Promise<Item> {
  const res = await api.POST('/v1/items/{itemId}/handoff', {
    params: { path: { itemId }, header: { 'Idempotency-Key': idempotencyKey() } },
    body: { employee_id: employeeId, note: blank(note) },
  });
  return itemFrom(unwrap(res), res.response.headers.get('ETag') ?? undefined);
}

/** Marks approved manual work as done, for example with the link to the published post. */
export async function reportDone(itemId: string, link?: string, note?: string): Promise<Item> {
  const res = await api.POST('/v1/items/{itemId}/result', {
    params: { path: { itemId }, header: { 'Idempotency-Key': idempotencyKey() } },
    body: { outcome: 'completed', link: blank(link), note: blank(note) },
  });
  return itemFrom(unwrap(res), res.response.headers.get('ETag') ?? undefined);
}

export async function history(f: { workspaceId?: string; cursor?: string } = {}): Promise<Page<ItemEvent>> {
  const res = unwrap(await api.GET('/v1/events', { params: { query: { workspace_id: f.workspaceId, cursor: f.cursor } } }));
  return { items: res.data.map(eventFrom).filter((e) => !echoesApproval(e)), nextCursor: res.next_cursor ?? undefined };
}
